import React, { useState, useEffect } from "react";
import { Tooltip, Dialog, FormControl, InputLabel, Select, MenuItem, CircularProgress } from "@mui/material";
import { useNotification } from "../../../context/NotificationContext";
import { useLayout } from "../../../context/LayoutContext";
import CopyIcon from "../../../assets/svg/components/copy.svg";
import EditIcon from "../../../assets/svg/view/edit.svg";
import DeleteIcon from "../../../assets/svg/components/trash.svg";
import AddIcon from "../../../assets/svg/components/archive.svg";
import SubdivisionIcon from "../../../assets/svg/components/archive.svg";
import LoadingAnimation from "../../loading/LoadingAnimation";
import PropTypes from "prop-types";
import { useOperationsHierarchy } from "../../../hooks/useDocumentationVehicleQuery";

// Función para truncar texto
function truncateText(text = "", maxLength = 30) {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}

export function OperationsTable({ fixedColumnsMode = 1 }) {
  // Acceso al tema actual
  const { theme } = useLayout();
  
  // Notificaciones
  const { showNotification } = useNotification();
  
  // Estados para filtrado
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  
  // Estados para el diálogo de crear/editar
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentOperation, setCurrentOperation] = useState(null);
  const [operationFormData, setOperationFormData] = useState({
    name: "",
    parent_id: "",
    role: "",
    original_name: "",
    route: "",
    logo_path: ""
  });
  
  // Estado para diálogo de divisiones
  const [divisionsDialogOpen, setDivisionsDialogOpen] = useState(false);
  const [parentOperation, setParentOperation] = useState(null);
  
  // Estados para el diálogo de confirmación de eliminación
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [operationToDelete, setOperationToDelete] = useState(null);
  
  // Estado para diálogo de migración
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);

  // Usar hook de operaciones jerárquicas
  const { 
    operations,
    isLoading, 
    isError,
    createOperation,
    updateOperation,
    deleteOperation,
    migrateOperations,
    isMigrating
  } = useOperationsHierarchy({
    role: roleFilter || null,
    search,
    includeInactive: false
  });

  // Mantener una lista plana para selección en formularios
  const [flatOperationsList, setFlatOperationsList] = useState([]);
  
  // Procesar operaciones para formato plano (para selección en forms)
  useEffect(() => {
    if (!operations) return;
    
    const flatList = [];
    
    const processOperations = (ops, level = 0, parentPath = "") => {
      ops.forEach(op => {
        const displayName = level > 0 
          ? `${"—".repeat(level)} ${op.name}`
          : op.name;
          
        flatList.push({
          id: op.id,
          name: displayName,
          level: level,
          parent_id: op.parent_id,
          path: parentPath ? `${parentPath} > ${op.name}` : op.name
        });
        
        if (op.divisions && op.divisions.length > 0) {
          processOperations(
            op.divisions, 
            level + 1, 
            parentPath ? `${parentPath} > ${op.name}` : op.name
          );
        }
      });
    };
    
    processOperations(operations);
    setFlatOperationsList(flatList);
  }, [operations]);

  // Manejador de búsqueda
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };
  
  // Manejador para filtro de rol
  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
  };

  // Copiar al portapapeles
  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showNotification({
          message: "¡Copiado exitosamente!",
          severity: "success",
        });
      })
      .catch(() => {
        showNotification({
          message: "Error al copiar.",
          severity: "error",
        });
      });
  };

  // Abrir diálogo para crear
  const handleCreate = (parentId = null) => {
    setIsEditing(false);
    setCurrentOperation(null);
    setOperationFormData({
      name: "",
      parent_id: parentId || "",
      role: roleFilter || "",
      original_name: "",
      route: "",
      logo_path: ""
    });
    setDialogOpen(true);
  };

  // Abrir diálogo para editar
  const handleEdit = (operation) => {
    setIsEditing(true);
    setCurrentOperation(operation);
    setOperationFormData({
      name: operation.name || "",
      parent_id: operation.parent_id || "",
      role: operation.role || "",
      original_name: operation.original_name || "",
      route: operation.route || "",
      logo_path: operation.logo_path || ""
    });
    setDialogOpen(true);
  };
  
  // Abrir diálogo para gestionar divisiones
  const handleManageDivisions = (operation) => {
    setParentOperation(operation);
    setDivisionsDialogOpen(true);
  };

  // Abrir diálogo para confirmar eliminación
  const handleDeleteClick = (operation) => {
    setOperationToDelete(operation);
    setDeleteDialogOpen(true);
  };

  // Cerrar diálogos
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDeleteDialogOpen(false);
    setDivisionsDialogOpen(false);
    setMigrationDialogOpen(false);
  };

  // Manejar cambios en el formulario
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setOperationFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Guardar operación (crear o actualizar)
  const handleSaveOperation = async () => {
    if (!operationFormData.name.trim()) {
      showNotification({
        message: "El nombre de la operación es requerido",
        severity: "error",
      });
      return;
    }

    try {
      // Filtrar solo campos con valor
      const dataToSend = Object.fromEntries(
        Object.entries(operationFormData)
          .filter(([_, value]) => value !== "")
      );
      
      if (isEditing && currentOperation) {
        await updateOperation({
          id: currentOperation.id,
          data: dataToSend
        });
        showNotification({
          message: "Operación actualizada exitosamente",
          severity: "success",
        });
      } else {
        await createOperation(dataToSend);
        showNotification({
          message: "Operación creada exitosamente",
          severity: "success",
        });
      }
      handleCloseDialog();
    } catch (error) {
      showNotification({
        message: error.message || "Error al procesar la operación",
        severity: "error",
      });
    }
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!operationToDelete) return;
    
    try {
      await deleteOperation(operationToDelete.id);
      showNotification({
        message: "Operación eliminada exitosamente",
        severity: "success",
      });
      handleCloseDialog();
    } catch (error) {
      showNotification({
        message: error.message || "Error al eliminar la operación",
        severity: "error",
      });
    }
  };
  
  // Ejecutar migración
  const handleMigrate = async () => {
    try {
      await migrateOperations();
      showNotification({
        message: "Migración completada exitosamente",
        severity: "success",
      });
      handleCloseDialog();
    } catch (error) {
      showNotification({
        message: error.message || "Error al realizar la migración",
        severity: "error",
      });
    }
  };
  
  // Función para renderizar operaciones de forma recursiva
  const renderOperationRows = (operations, level = 0) => {
    if (!operations || operations.length === 0) return null;
    
    return operations.map((operation) => {
      const hasSubdivisions = operation.divisions && operation.divisions.length > 0;
      
      return (
        <React.Fragment key={operation.id}>
          <tr className={level > 0 ? `GM__${theme}-child-row level-${level}` : ""}>
            {/* Nombre: Truncado, Tooltip y copiable */}
            <td
              className={`GM__${theme}-copyable-cell ${
                fixedColumnsMode === 1 || fixedColumnsMode === 3
                  ? "fixed-column fixed-column-1"
                  : ""
              } ${level > 0 ? "indented-cell" : ""}`}
              style={{ paddingLeft: `${level * 20 + 8}px` }}
            >
              <Tooltip title={operation.name || "—"}>
                <span>
                  {hasSubdivisions && (
                    <span className={`GM__${theme}-has-children-icon`}>▸ </span>
                  )}
                  {truncateText(operation.name || "", 50)}
                </span>
              </Tooltip>
              <button
                className={`GM__${theme}-copy-icon`}
                onClick={() => handleCopy(operation.name)}
                aria-label="Copiar nombre"
              >
                <img src={CopyIcon} alt="Copiar" />
              </button>
            </td>

            {/* Rol */}
            <td>{operation.role || "—"}</td>
            
            {/* Ruta */}
            <td>
              {operation.route ? (
                <Tooltip title={operation.route}>
                  <span>{truncateText(operation.route || "", 30)}</span>
                </Tooltip>
              ) : (
                "—"
              )}
            </td>
            
            {/* Fecha de creación */}
            <td>
              {operation.created_at ? new Date(operation.created_at).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              }) : "—"}
            </td>

            {/* Acciones */}
            <td className={`GM__${theme}-action-buttons`}>
              <Tooltip title="Gestionar divisiones">
                <button
                  className={`GM__${theme}-subdivision-button`}
                  onClick={() => handleManageDivisions(operation)}
                  aria-label="Gestionar divisiones"
                >
                  <img src={SubdivisionIcon} alt="Divisiones" className={`GM__${theme}-button-icon`} />
                </button>
              </Tooltip>
              <Tooltip title="Editar">
                <button
                  className={`GM__${theme}-edit-button`}
                  onClick={() => handleEdit(operation)}
                  aria-label="Editar"
                >
                  <img src={EditIcon} alt="Editar" className={`GM__${theme}-button-icon`} />
                </button>
              </Tooltip>
              <Tooltip title="Eliminar">
                <button
                  className={`GM__${theme}-delete-button`}
                  onClick={() => handleDeleteClick(operation)}
                  aria-label="Eliminar"
                >
                  <img src={DeleteIcon} alt="Eliminar" className={`GM__${theme}-button-icon`} />
                </button>
              </Tooltip>
            </td>
          </tr>
          
          {/* Renderizar subdivisiones si existen */}
          {hasSubdivisions && renderOperationRows(operation.divisions, level + 1)}
        </React.Fragment>
      );
    });
  };

  // Renderizar indicadores de carga y error
  if (isLoading) return <LoadingAnimation />;
  if (isError) return <div className={`GM__${theme}-error-message`}>Hubo un error al cargar las operaciones.</div>;

  return (
    <div className={`GM__${theme}-vehicle-table-wrapper`}>
      <div className={`GM__${theme}-header-section`}>
        <h2 className={`GM__${theme}-section-title`}>Operaciones</h2>
        <div className={`GM__${theme}-filter-container`}>
          <input
            type="text"
            placeholder="Buscar por nombre..."
            value={search}
            onChange={handleSearchChange}
            className={`GM__${theme}-search-input`}
          />
          <FormControl variant="outlined" size="small" className={`GM__${theme}-role-filter`}>
            <InputLabel id="role-filter-label">Rol</InputLabel>
            <Select
              labelId="role-filter-label"
              id="role-filter"
              value={roleFilter}
              onChange={handleRoleFilterChange}
              label="Rol"
              displayEmpty
              className={`GM__${theme}-select-input`}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="log">Logística (log)</MenuItem>
              <MenuItem value="admin">Administración</MenuItem>
              <MenuItem value="maint">Mantenimiento</MenuItem>
            </Select>
          </FormControl>
          <div className={`GM__${theme}-button-group`}>
            <button 
              className={`GM__${theme}-add-button`}
              onClick={() => handleCreate()}
            >
              <img src={AddIcon} alt="Añadir" className={`GM__${theme}-button-icon`} />
              Nueva Operación
            </button>
          </div>
        </div>
      </div>

      <div className={`GM__${theme}-vehicle-table-wrapper-table`}>
        <table className={`GM__${theme}-vehicle-table fixed-columns-${fixedColumnsMode} hierarchical-table`}>
          <thead>
            <tr>
              <th
                className={`${
                  fixedColumnsMode === 1 || fixedColumnsMode === 3
                    ? "fixed-column fixed-column-1"
                    : ""
                }`}
              >
                Nombre
              </th>
              <th>Rol</th>
              <th>Ruta</th>
              <th>Fecha Creación</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {operations && operations.length > 0 ? (
              renderOperationRows(operations)
            ) : (
              <tr>
                <td colSpan={5} className={`GM__${theme}-empty-message`}>
                  No se encontraron operaciones
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Diálogo para crear/editar operación */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        className={`GM__modal-container-${theme}`}
        BackdropProps={{
          style: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        }}
      >
        <div className={`GM__${theme}-modal GM__${theme}-operation-modal`}>
          <div className={`GM__${theme}-modal-header-container`}>
            <h3 className={`GM__${theme}-modal-header`}>
              {isEditing ? "Editar Operación" : "Crear Nueva Operación"}
            </h3>
          </div>
          <div className={`GM__${theme}-modal-content`}>
            <div className={`GM__${theme}-form-row`}>
              <div className={`GM__${theme}-form-group full-width`}>
                <label className={`GM__${theme}-form-label`} htmlFor="name">
                  Nombre de la Operación *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={operationFormData.name}
                  onChange={handleFormChange}
                  className={`GM__${theme}-form-input`}
                  placeholder="Ingrese el nombre de la operación"
                  autoFocus
                  required
                />
              </div>
            </div>
            
            <div className={`GM__${theme}-form-row`}>
              <div className={`GM__${theme}-form-group half-width`}>
                <label className={`GM__${theme}-form-label`} htmlFor="parent_id">
                  Operación Padre
                </label>
                <select
                  id="parent_id"
                  name="parent_id"
                  value={operationFormData.parent_id}
                  onChange={handleFormChange}
                  className={`GM__${theme}-form-select`}
                >
                  <option value="">-- Ninguna (Operación Principal) --</option>
                  {flatOperationsList
                    .filter(op => op.id !== (currentOperation?.id || ''))
                    .map(op => (
                      <option key={op.id} value={op.id}>
                        {op.name}
                      </option>
                    ))
                  }
                </select>
              </div>
              
              <div className={`GM__${theme}-form-group half-width`}>
                <label className={`GM__${theme}-form-label`} htmlFor="role">
                  Rol
                </label>
                <select
                  id="role"
                  name="role"
                  value={operationFormData.role}
                  onChange={handleFormChange}
                  className={`GM__${theme}-form-select`}
                >
                  <option value="">-- Sin rol --</option>
                  <option value="log">Logística (log)</option>
                  <option value="admin">Administración</option>
                  <option value="maint">Mantenimiento</option>
                </select>
              </div>
            </div>
            
            <div className={`GM__${theme}-form-row`}>
              <div className={`GM__${theme}-form-group half-width`}>
                <label className={`GM__${theme}-form-label`} htmlFor="route">
                  Ruta
                </label>
                <input
                  id="route"
                  name="route"
                  type="text"
                  value={operationFormData.route}
                  onChange={handleFormChange}
                  className={`GM__${theme}-form-input`}
                  placeholder="/scheduler/operation-name"
                />
              </div>
              
              <div className={`GM__${theme}-form-group half-width`}>
                <label className={`GM__${theme}-form-label`} htmlFor="logo_path">
                  Ruta del Logo
                </label>
                <input
                  id="logo_path"
                  name="logo_path"
                  type="text"
                  value={operationFormData.logo_path}
                  onChange={handleFormChange}
                  className={`GM__${theme}-form-input`}
                  placeholder="/assets/logos/operation-logo.svg"
                />
              </div>
            </div>
            
            {isEditing && (
              <div className={`GM__${theme}-form-row`}>
                <div className={`GM__${theme}-form-group full-width`}>
                  <label className={`GM__${theme}-form-label`} htmlFor="original_name">
                    Nombre Original
                  </label>
                  <input
                    id="original_name"
                    name="original_name"
                    type="text"
                    value={operationFormData.original_name}
                    onChange={handleFormChange}
                    className={`GM__${theme}-form-input`}
                    placeholder="Nombre original (si aplica)"
                  />
                </div>
              </div>
            )}
          </div>
          <div className={`GM__${theme}-modal-footer`}>
            <button 
              className={`GM__${theme}-btn GM__${theme}-btn-cancel`} 
              onClick={handleCloseDialog}
            >
              Cancelar
            </button>
            <button 
              className={`GM__${theme}-btn GM__${theme}-btn-save`} 
              onClick={handleSaveOperation}
            >
              {isEditing ? "Actualizar" : "Crear"}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Diálogo para gestionar divisiones */}
      <Dialog 
        open={divisionsDialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        className={`GM__modal-container-${theme}`}
        BackdropProps={{
          style: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        }}
      >
        <div className={`GM__${theme}-modal GM__${theme}-divisions-modal`}>
          <div className={`GM__${theme}-modal-header-container`}>
            <h3 className={`GM__${theme}-modal-header`}>
              Divisiones de {parentOperation?.name || ''}
            </h3>
          </div>
          <div className={`GM__${theme}-modal-content`}>
            <div className={`GM__${theme}-divisions-list`}>
              {parentOperation && parentOperation.divisions && parentOperation.divisions.length > 0 ? (
                <div className={`GM__${theme}-divisions-container`}>
                  {parentOperation.divisions.map(division => (
                    <div key={division.id} className={`GM__${theme}-division-item`}>
                      <div className={`GM__${theme}-division-info`}>
                        <span className={`GM__${theme}-division-name`}>{division.name}</span>
                        <span className={`GM__${theme}-division-route`}>{division.route || '—'}</span>
                      </div>
                      <div className={`GM__${theme}-division-actions`}>
                        <button
                          className={`GM__${theme}-edit-button`}
                          onClick={() => {
                            handleCloseDialog();
                            handleEdit(division);
                          }}
                          aria-label="Editar"
                        >
                          <img src={EditIcon} alt="Editar" className={`GM__${theme}-button-icon`} />
                        </button>
                        <button
                          className={`GM__${theme}-delete-button`}
                          onClick={() => {
                            handleCloseDialog();
                            handleDeleteClick(division);
                          }}
                          aria-label="Eliminar"
                        >
                          <img src={DeleteIcon} alt="Eliminar" className={`GM__${theme}-button-icon`} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`GM__${theme}-empty-divisions`}>
                  No hay divisiones para esta operación
                </div>
              )}
            </div>
          </div>
          <div className={`GM__${theme}-modal-footer`}>
            <button 
              className={`GM__${theme}-btn GM__${theme}-btn-cancel`} 
              onClick={handleCloseDialog}
            >
              Cerrar
            </button>
            <button 
              className={`GM__${theme}-btn GM__${theme}-btn-save`} 
              onClick={() => {
                handleCloseDialog();
                handleCreate(parentOperation?.id);
              }}
            >
              Añadir División
            </button>
          </div>
        </div>
      </Dialog>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="sm"
        className={`GM__modal-container-${theme}`}
        BackdropProps={{
          style: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        }}
      >
        <div className={`GM__${theme}-modal GM__${theme}-confirm-modal`}>
          <div className={`GM__${theme}-modal-header-container`}>
            <h3 className={`GM__${theme}-modal-header`}>
              Confirmar Eliminación
            </h3>
          </div>
          <div className={`GM__${theme}-modal-content`}>
            <p>¿Está seguro que desea eliminar la operación "{operationToDelete?.name}"?</p>
            <p className={`GM__${theme}-warning-text`}>
              Esta acción no se puede deshacer. Se eliminarán también todas las divisiones asociadas.
            </p>
            {operationToDelete?.divisions?.length > 0 && (
              <div className={`GM__${theme}-warning-box`}>
                <p><strong>¡Atención!</strong> Esta operación tiene {operationToDelete.divisions.length} divisiones:</p>
                <ul>
                  {operationToDelete.divisions.map(division => (
                    <li key={division.id}>{division.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className={`GM__${theme}-modal-footer`}>
            <button 
              className={`GM__${theme}-btn GM__${theme}-btn-cancel`} 
              onClick={handleCloseDialog}
            >
              Cancelar
            </button>
            <button 
              className={`GM__${theme}-btn GM__${theme}-btn-danger`} 
              onClick={handleConfirmDelete}
            >
              Eliminar
            </button>
          </div>
        </div>
      </Dialog>
      
      {/* Diálogo para migración de datos */}
      <Dialog 
        open={migrationDialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="sm"
        className={`GM__modal-container-${theme}`}
        BackdropProps={{
          style: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        }}
      >
        <div className={`GM__${theme}-modal GM__${theme}-migration-modal`}>
          <div className={`GM__${theme}-modal-header-container`}>
            <h3 className={`GM__${theme}-modal-header`}>
              Migración de Datos Existentes
            </h3>
          </div>
          <div className={`GM__${theme}-modal-content`}>
            <p>Esta acción migrará los datos existentes a la nueva estructura jerárquica.</p>
            <p>Se realizarán las siguientes transformaciones:</p>
            <ul>
              <li><strong>Terpel</strong>: Se mantendrá y se crearán divisiones "Norte" y "Sur"</li>
              <li><strong>Ventas Emerald</strong>: Pasará a ser "Emerald" bajo "Impala"</li>
              <li><strong>Ventas Costayaco</strong>: Pasará a ser "Gran Tierra Energy" bajo "Impala"</li>
              <li><strong>Regalías</strong>: Pasará a ser "Ecopetrol"</li>
            </ul>
            <p className={`GM__${theme}-warning-text`}>
              Esta operación solo debe ejecutarse una vez. ¿Desea continuar?
            </p>
            {isMigrating && (
              <div className={`GM__${theme}-loading-container`}>
                <CircularProgress size={24} />
                <span>Migrando datos...</span>
              </div>
            )}
          </div>
          <div className={`GM__${theme}-modal-footer`}>
            <button 
              className={`GM__${theme}-btn GM__${theme}-btn-cancel`} 
              onClick={handleCloseDialog}
              disabled={isMigrating}
            >
              Cancelar
            </button>
            <button 
              className={`GM__${theme}-btn GM__${theme}-btn-primary`} 
              onClick={handleMigrate}
              disabled={isMigrating}
            >
              Migrar Datos
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

OperationsTable.propTypes = {
  fixedColumnsMode: PropTypes.oneOf([1, 2, 3]),
};